import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select, Modal, Tooltip, InputNumber } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { getAllClientsOfDairyAction, updateClientBalanceAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Option } = Select;

const EditableBalanceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BalanceText = styled.span`
  cursor: pointer;
  &:hover {
    color: #1890ff;
  }
`;

export const BasicTable: React.FC = () => {
  const [filters, setFilters] = useState({
    clientName: '',
    deliveryDateRange: [],
    deliveryStatus: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const [editingBalanceId, setEditingBalanceId] = useState<string | null>(null);
  const [editingBalance, setEditingBalance] = useState<number>(0);
  const dispatch = useAppDispatch();
  let navigate = useNavigate();

  // Fetch data with updated filters and pagination
  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      deliveryDateRange: filters.deliveryDateRange.length
        ? [
          moment(filters.deliveryDateRange[0]).toISOString(),
          moment(filters.deliveryDateRange[1]).toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAllClientsOfDairyAction(params)).unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, dispatch]);



  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }

    window.addEventListener('error', hideError)
    return () => {
      window.addEventListener('error', hideError)
    }
  }, [])

  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  // Handle balance edit
  const handleEditBalance = (record: any) => {
    setEditingBalanceId(record._id);
    setEditingBalance(record.balance || 0);
  };

  // Handle balance save
  const handleSaveBalance = (record: any) => {
    Modal.confirm({
      title: 'Confirm Balance Update',
      content: `Are you sure you want to update the wallet balance from ₹${record.balance} to ₹${editingBalance}?`,
      onOk: () => {
        dispatch(updateClientBalanceAction({ clientId: record._id, balance: editingBalance }))
          .unwrap()
          .then((response) => {
            if (response.success) {
              notificationController.success({ message: 'Balance updated successfully' });
              setEditingBalanceId(null);
              fetchData();
            } else {
              notificationController.error({ message: response.message || 'Failed to update balance' });
            }
          })
          .catch((error) => {
            notificationController.error({ message: error || 'Failed to update balance' });
          });
      },
    });
  };

  // Handle balance cancel
  const handleCancelBalance = () => {
    setEditingBalanceId(null);
    setEditingBalance(0);
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text, record) => <span>{record?.name || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Name"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('name', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      )
    },

    {
      title: 'Mobile Number',
      dataIndex: 'phone',
      render: (text, record) => <span>{record?.phone || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Mobile Number"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('phone', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => record?.phone.includes(value),
    },



    {
      title: 'Wallet Balance',
      dataIndex: 'balance',
      render: (text, record) => {
        const isEditing = editingBalanceId === record._id;

        return (
          <EditableBalanceWrapper>
            {isEditing ? (
              <>
                <InputNumber
                  value={editingBalance}
                  onChange={(value) => setEditingBalance(value || 0)}
                  style={{ width: 120 }}
                  min={0}
                  precision={2}
                  autoFocus
                />
                <Tooltip title="Save">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() => handleSaveBalance(record)}
                  />
                </Tooltip>
                <Tooltip title="Cancel">
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={handleCancelBalance}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <BalanceText>{record?.balance || 0}</BalanceText>
                <Tooltip title="Edit Balance">
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditBalance(record)}
                  />
                </Tooltip>
              </>
            )}
          </EditableBalanceWrapper>
        );
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search balance"
            value={selectedKeys[0]}
            type='number'
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('balance', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      )
    },
    {
      title: "Actions",
      dataIndex: 'actions',
      width: '15%',
      render: (text: string, record: any) => {
        return (
          <BaseSpace>
            <BaseButton
              type="danger"
              onClick={() => {
                navigate('addresses/' + record._id)
              }}
            >
              Manage Address
            </BaseButton>

          </BaseSpace>
        );
      },
    },
  ];

  return (
    <>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          setFilters({
            clientName: '',
            deliveryDateRange: [],
            deliveryStatus: ''
          });

          setTimeout(() => {
            setPagination({ current: 1, pageSize: 20 });

          }, 1000);
        }}>Clear Filters</Button>
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
    </>
  );
};
